import { books } from './bibleBooks'

const escapeRegex = (str: string) => {
	return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Ordinal / roman-numeral prefixes so "2nd Kings", "II Kings" and "Second Kings"
// linkify exactly like "2 Kings". Generated for any book whose name starts 1/2/3.
const ORDINAL_FORMS: Record<string, string[]> = {
	'1': ['1st', 'I', 'First'],
	'2': ['2nd', 'II', 'Second'],
	'3': ['3rd', 'III', 'Third'],
}

const bookAliases = (book: { name: string; abbreviations?: string[] }): string[] => {
	const aliases = [book.name, ...(book.abbreviations || [])]
	const match = book.name.match(/^([123])\s+(.+)$/)
	if (match) {
		const [, num, rest] = match
		ORDINAL_FORMS[num].forEach((form) => aliases.push(`${form} ${rest}`))
	}
	return aliases
}

const buildAbbreviationMap = () => {
	const map: Record<string, string> = {}
	for (const book of books) {
		for (const alias of bookAliases(book)) {
			map[alias] = book.name
		}
	}
	return map
}

const abbreviationMap = buildAbbreviationMap()

const buildBookPatterns = () => {
	const patterns: string[] = []
	for (const book of books) {
		for (const alias of bookAliases(book)) {
			patterns.push(escapeRegex(alias))
		}
	}
	// Sort longest first so "Second Kings"/"1 Cor." match before shorter prefixes.
	patterns.sort((a, b) => b.length - a.length)
	return patterns
}

const CHAPTER_VERSE_REGEX = '([0-9]{1,3}:[0-9]{1,3}(([-–,]?(?:[0-9]{1,3}))*)?)(?=(?:[^><]*<[^<>]*>)*[^><]*$)'
const FULL_REGEX = new RegExp('(' + buildBookPatterns().join('|') + ')\\s+' + CHAPTER_VERSE_REGEX, 'gm')

const generateIdFromContent = (content: string) => {
	// Remove special characters and spaces, convert to lowercase
	return content
		.replace(/[^\w\s]/gi, '')
		.replace(/\s+/g, '-')
		.toLowerCase()
}

const tagHeadersWithIds = (text: string) => {
	const headerRegex = /<h2>(.*?)<\/h2>/g

	return text.replace(headerRegex, (match, p1) => {
		const id = generateIdFromContent(p1)
		return `<h2 id="${id}">${p1}</h2>`
	})
}

const tagBibleVerses = (text: string) => {
	// Tag <h2> headers with IDs based on their content
	const taggedWithHeaders = tagHeadersWithIds(text)

	FULL_REGEX.lastIndex = 0

	const taggedText = taggedWithHeaders.replace(FULL_REGEX, (match, bookMatch, chapterVerse) => {
		const canonicalName = abbreviationMap[bookMatch] || bookMatch
		return `<span class='bible-verse-tag'>${canonicalName} ${chapterVerse}</span>`
	})

	return taggedText
}

const extractH2ContentWithIds = (text: string) => {
	const headerRegex = /<h2>(.*?)<\/h2>/g
	const headersWithIds: { id: string; content: string }[] = []

	text.replace(headerRegex, (match, p1) => {
		const id = generateIdFromContent(p1)
		headersWithIds.push({ id, content: p1 })
		return match // Return the original match to maintain text integrity
	})

	return headersWithIds
}

/**
 * Extracts all unique Bible chapter keys (bookId:chapter) from an HTML string.
 * Used to preload and cache every scripture chapter referenced in a manual's
 * body, summary, and memory track before the user taps them.
 */
const extractBibleChapterKeys = (html: string): Array<{ bookId: string; chapter: string }> => {
	FULL_REGEX.lastIndex = 0

	const seen = new Set<string>()
	const keys: Array<{ bookId: string; chapter: string }> = []

	let match: RegExpExecArray | null
	while ((match = FULL_REGEX.exec(html)) !== null) {
		const canonicalName = abbreviationMap[match[1]] || match[1]
		// chapterVerse is like "3:16-18" — we only need the chapter number
		const chapter = match[2].split(':')[0]

		const bookObj = books.find((b) => b.name === canonicalName)
		if (!bookObj) continue

		const key = `${bookObj.id}_${chapter}`
		if (!seen.has(key)) {
			seen.add(key)
			keys.push({ bookId: String(bookObj.id), chapter })
		}
	}

	FULL_REGEX.lastIndex = 0
	return keys
}

export { tagBibleVerses, extractH2ContentWithIds, extractBibleChapterKeys }
